import { useEffect, useMemo, useState } from 'react'
import { Check, Plus, Trash2, X } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Select,
  useToast,
} from '@/components/ui'
import {
  useAddPricingTier,
  useAddTariffComponent,
  useDeletePricingTier,
  useDeleteTariffComponent,
  useProducts,
  useTariff,
  useUpdatePricingTier,
  useUpdateTariffComponent,
} from '../api/contractsApi.ts'
import type { PricingTier, TariffComponent } from '../types.ts'

interface TariffEditorProps {
  tariffId: string
  productUnit: string
}

function centsToEuroString(cents: number | null | undefined): string {
  if (cents == null) return ''
  return (cents / 100).toFixed(2).replace('.', ',')
}

function euroStringToCents(s: string): number | null {
  const trimmed = s.trim()
  if (!trimmed) return null
  const n = parseFloat(trimmed.replace(/\./g, '').replace(',', '.'))
  if (Number.isNaN(n)) return null
  return Math.round(n * 100)
}

function qtyX1000ToString(q: number | null | undefined): string {
  if (q == null) return ''
  return (q / 1000).toLocaleString('de-DE', { maximumFractionDigits: 3 })
}

function stringToQtyX1000(s: string): number | null {
  const trimmed = s.trim()
  if (!trimmed) return null
  const n = parseFloat(trimmed.replace(/\./g, '').replace(',', '.'))
  if (Number.isNaN(n)) return null
  return Math.round(n * 1000)
}

function fmtEur(cents: number | null | undefined, currency = 'EUR'): string {
  if (cents == null) return '—'
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency })
}

export function TariffEditor({ tariffId, productUnit }: TariffEditorProps) {
  const { data: tariff, isLoading, error, refetch } = useTariff(tariffId)
  const [addOpen, setAddOpen] = useState(false)

  if (isLoading) return <LoadingState label="Lade Tarif …" />
  if (error) {
    return (
      <ErrorState
        description="Tarif konnte nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }
  if (!tariff) return <EmptyState title="Tarif nicht gefunden" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-small text-gray-600">
          {tariff.components.length} Komponente{tariff.components.length === 1 ? '' : 'n'} im Tarif
        </p>
        <Button variant="secondary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setAddOpen(true)}>
          Komponente hinzufügen
        </Button>
      </div>

      {tariff.components.length === 0 ? (
        <EmptyState
          title="Noch keine Komponenten"
          description="Eine Komponente verknüpft den Tarif mit einem konkreten Vertragsbaustein (Lizenz, Service, Asset) und legt den Verkaufspreis fest. Optionale Mengenstaffeln pro Komponente."
        />
      ) : (
        <div className="space-y-3">
          {tariff.components.map((c) => (
            <ComponentRow
              key={c.id}
              tariffId={tariffId}
              currency={tariff.currency}
              component={c}
              fallbackUnit={productUnit}
            />
          ))}
        </div>
      )}

      <AddComponentModal
        open={addOpen}
        onOpenChange={setAddOpen}
        tariffId={tariffId}
        excludeProductIds={tariff.components.map((c) => c.componentProductId)}
      />
    </div>
  )
}

// ─── Komponente in einem Tarif ─────────────────────────────────────────────

interface ComponentRowProps {
  tariffId: string
  currency: string
  component: TariffComponent
  fallbackUnit: string
}

function ComponentRow({ tariffId, currency, component, fallbackUnit }: ComponentRowProps) {
  const { toast } = useToast()
  const update = useUpdateTariffComponent(tariffId)
  const del = useDeleteTariffComponent(tariffId)
  const [unitPrice, setUnitPrice] = useState(centsToEuroString(component.unitPriceCents))
  const [unitCost, setUnitCost] = useState(centsToEuroString(component.unitCostCents))
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    setUnitPrice(centsToEuroString(component.unitPriceCents))
    setUnitCost(centsToEuroString(component.unitCostCents))
  }, [component.unitPriceCents, component.unitCostCents])

  const dirty =
    unitPrice !== centsToEuroString(component.unitPriceCents) ||
    unitCost !== centsToEuroString(component.unitCostCents)

  function handleSave() {
    const priceCents = euroStringToCents(unitPrice)
    if (priceCents == null) {
      toast({ title: 'Verkaufspreis ungültig', tone: 'danger' })
      return
    }
    update.mutate(
      {
        componentId: component.id,
        input: {
          unitPriceCents: priceCents,
          unitCostCents: unitCost.trim() ? euroStringToCents(unitCost) : null,
        },
      },
      {
        onSuccess: () => toast({ title: 'Komponente gespeichert', tone: 'success' }),
        onError: (err) =>
          toast({
            title: 'Speichern fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  function takeSapPurchasePrice() {
    if (!component.sapArticle) return
    setUnitCost(centsToEuroString(component.sapArticle.purchasePriceCents))
  }

  const unit = component.product?.unit ?? fallbackUnit

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-body font-semibold text-gray-900">
              {component.product?.name ?? '— Produkt fehlt —'}
            </h4>
            <p className="text-caption text-gray-500">
              Einheit: {unit}
              {component.product?.sku ? ` · SKU ${component.product.sku}` : ''}
              {component.product?.sapItemCode ? (
                <>
                  {' · '}
                  <Badge variant="info" size="sm">
                    SAP {component.product.sapItemCode}
                  </Badge>
                </>
              ) : null}
            </p>
          </div>
          <IconButton
            icon={<Trash2 className="w-4 h-4" />}
            aria-label="Komponente entfernen"
            variant="ghost"
            size="sm"
            onClick={() => setConfirmDelete(true)}
          />
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Verkaufspreis je Einheit"
            hint={`pro ${unit}`}
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.currentTarget.value)}
            trailingIcon={<span className="text-caption text-gray-500">{currency}</span>}
          />
          <div>
            <Input
              label="Einkaufspreis je Einheit"
              hint="optional, für Margenrechnung"
              value={unitCost}
              onChange={(e) => setUnitCost(e.currentTarget.value)}
              trailingIcon={<span className="text-caption text-gray-500">{currency}</span>}
            />
            {component.sapArticle ? (
              <button
                type="button"
                onClick={takeSapPurchasePrice}
                className="mt-1 text-caption text-brand-700 hover:underline"
              >
                EK aus SAP übernehmen ({fmtEur(component.sapArticle.purchasePriceCents, currency)})
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          {dirty ? (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<X className="w-4 h-4" />}
              onClick={() => {
                setUnitPrice(centsToEuroString(component.unitPriceCents))
                setUnitCost(centsToEuroString(component.unitCostCents))
              }}
            >
              Verwerfen
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Check className="w-4 h-4" />}
            disabled={!dirty}
            loading={update.isPending}
            onClick={handleSave}
          >
            Preise speichern
          </Button>
        </div>

        <TiersEditor
          tariffId={tariffId}
          componentId={component.id}
          currency={currency}
          unit={unit}
          tiers={component.tiers}
        />
      </Card.Body>

      <ConfirmDialog
        open={confirmDelete}
        title="Komponente entfernen?"
        message={`„${component.product?.name ?? 'Komponente'}" wird inklusive Mengenstaffeln aus dem Tarif entfernt.`}
        variant="danger"
        confirmLabel="Entfernen"
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(component.id, {
            onSuccess: () => {
              toast({ title: 'Komponente entfernt', tone: 'success' })
              setConfirmDelete(false)
            },
            onError: (err) =>
              toast({
                title: 'Entfernen fehlgeschlagen',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              }),
          })
        }
        onCancel={() => setConfirmDelete(false)}
      />
    </Card>
  )
}

// ─── Mengenstaffeln pro Komponente ─────────────────────────────────────────

interface TiersEditorProps {
  tariffId: string
  componentId: string
  currency: string
  unit: string
  tiers: PricingTier[]
}

function TiersEditor({ tariffId, componentId, currency, unit, tiers }: TiersEditorProps) {
  const { toast } = useToast()
  const add = useAddPricingTier(tariffId)
  const update = useUpdatePricingTier(tariffId)
  const del = useDeletePricingTier(tariffId)

  const [newMin, setNewMin] = useState('')
  const [newMax, setNewMax] = useState('')
  const [newPrice, setNewPrice] = useState('')

  function handleAdd() {
    const minQty = stringToQtyX1000(newMin) ?? 0
    const maxQty = newMax.trim() ? stringToQtyX1000(newMax) : null
    const priceCents = euroStringToCents(newPrice)
    if (priceCents == null) {
      toast({ title: 'Preis ungültig', tone: 'danger' })
      return
    }
    if (maxQty != null && maxQty <= minQty) {
      toast({ title: 'Max muss größer als Min sein', tone: 'danger' })
      return
    }
    add.mutate(
      {
        componentId,
        input: { minQtyX1000: minQty, maxQtyX1000: maxQty, unitPriceCents: priceCents },
      },
      {
        onSuccess: () => {
          setNewMin('')
          setNewMax('')
          setNewPrice('')
          toast({ title: 'Staffel ergänzt', tone: 'success' })
        },
        onError: (err) =>
          toast({
            title: 'Anlegen fehlgeschlagen',
            description: err instanceof Error ? err.message : 'Unbekannter Fehler',
            tone: 'danger',
          }),
      },
    )
  }

  return (
    <div className="space-y-2 pt-2 border-t border-gray-100">
      <p className="text-small font-medium text-gray-700">Mengenstaffeln</p>

      {tiers.length === 0 ? (
        <p className="text-caption text-gray-500">
          Keine Staffeln — Verkaufspreis oben gilt für jede Menge.
        </p>
      ) : (
        <div className="space-y-1">
          {tiers.map((t) => (
            <TierRow
              key={t.id}
              tier={t}
              unit={unit}
              currency={currency}
              onUpdate={(input) =>
                update.mutate(
                  { tierId: t.id, input },
                  {
                    onSuccess: () => toast({ title: 'Staffel gespeichert', tone: 'success' }),
                    onError: (err) =>
                      toast({
                        title: 'Speichern fehlgeschlagen',
                        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                        tone: 'danger',
                      }),
                  },
                )
              }
              onDelete={() =>
                del.mutate(t.id, {
                  onSuccess: () => toast({ title: 'Staffel entfernt', tone: 'success' }),
                  onError: (err) =>
                    toast({
                      title: 'Entfernen fehlgeschlagen',
                      description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                      tone: 'danger',
                    }),
                })
              }
              busy={update.isPending || del.isPending}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end pt-2">
        <Input
          label="ab Menge"
          value={newMin}
          onChange={(e) => setNewMin(e.currentTarget.value)}
          placeholder="z. B. 10"
          trailingIcon={<span className="text-caption text-gray-500">{unit}</span>}
        />
        <Input
          label="bis Menge"
          hint="leer = unbegrenzt"
          value={newMax}
          onChange={(e) => setNewMax(e.currentTarget.value)}
          placeholder="z. B. 24"
          trailingIcon={<span className="text-caption text-gray-500">{unit}</span>}
        />
        <Input
          label="Preis je Einheit"
          value={newPrice}
          onChange={(e) => setNewPrice(e.currentTarget.value)}
          trailingIcon={<span className="text-caption text-gray-500">{currency}</span>}
        />
        <Button variant="secondary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={handleAdd} loading={add.isPending}>
          Staffel hinzufügen
        </Button>
      </div>
    </div>
  )
}

// ─── Einzelne Staffel-Zeile (inline editierbar) ────────────────────────────

interface TierRowProps {
  tier: PricingTier
  unit: string
  currency: string
  onUpdate: (input: { minQtyX1000?: number; maxQtyX1000?: number | null; unitPriceCents?: number }) => void
  onDelete: () => void
  busy: boolean
}

function TierRow({ tier, unit, currency, onUpdate, onDelete, busy }: TierRowProps) {
  const [minStr, setMinStr] = useState(qtyX1000ToString(tier.minQtyX1000))
  const [maxStr, setMaxStr] = useState(qtyX1000ToString(tier.maxQtyX1000))
  const [priceStr, setPriceStr] = useState(centsToEuroString(tier.unitPriceCents))

  useEffect(() => {
    setMinStr(qtyX1000ToString(tier.minQtyX1000))
    setMaxStr(qtyX1000ToString(tier.maxQtyX1000))
    setPriceStr(centsToEuroString(tier.unitPriceCents))
  }, [tier.minQtyX1000, tier.maxQtyX1000, tier.unitPriceCents])

  const dirty =
    minStr !== qtyX1000ToString(tier.minQtyX1000) ||
    maxStr !== qtyX1000ToString(tier.maxQtyX1000) ||
    priceStr !== centsToEuroString(tier.unitPriceCents)

  function handleSave() {
    onUpdate({
      minQtyX1000: stringToQtyX1000(minStr) ?? 0,
      maxQtyX1000: maxStr.trim() ? stringToQtyX1000(maxStr) : null,
      unitPriceCents: euroStringToCents(priceStr) ?? 0,
    })
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center bg-gray-50 rounded-md px-2 py-1.5">
      <Input
        wrapperClassName="m-0"
        value={minStr}
        onChange={(e) => setMinStr(e.currentTarget.value)}
        trailingIcon={<span className="text-caption text-gray-500">{unit}</span>}
      />
      <Input
        wrapperClassName="m-0"
        value={maxStr}
        onChange={(e) => setMaxStr(e.currentTarget.value)}
        placeholder="∞"
        trailingIcon={<span className="text-caption text-gray-500">{unit}</span>}
      />
      <Input
        wrapperClassName="m-0"
        value={priceStr}
        onChange={(e) => setPriceStr(e.currentTarget.value)}
        trailingIcon={<span className="text-caption text-gray-500">{currency}</span>}
      />
      <div className="flex justify-end gap-1 md:col-span-2">
        {dirty ? (
          <Button variant="primary" size="xs" onClick={handleSave} loading={busy}>
            Speichern
          </Button>
        ) : null}
        <IconButton
          icon={<Trash2 className="w-3.5 h-3.5" />}
          aria-label="Staffel entfernen"
          variant="ghost"
          size="sm"
          onClick={onDelete}
        />
      </div>
    </div>
  )
}

// ─── Komponente hinzufuegen (Produkt-Picker) ────────────────────────────────

interface AddComponentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tariffId: string
  excludeProductIds: string[]
}

function AddComponentModal({ open, onOpenChange, tariffId, excludeProductIds }: AddComponentModalProps) {
  const { toast } = useToast()
  const { data: products = [] } = useProducts({ active: true })
  const add = useAddTariffComponent(tariffId)
  const [productId, setProductId] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setProductId('')
      setUnitPrice('')
      setError(null)
    }
  }, [open])

  const options = useMemo(
    () => [
      { value: '', label: '— Produkt wählen —' },
      ...products
        .filter((p) => !excludeProductIds.includes(p.id))
        .map((p) => ({ value: p.id, label: `${p.name}${p.sku ? ` · ${p.sku}` : ''}` })),
    ],
    [products, excludeProductIds],
  )

  function handleSave() {
    if (!productId) {
      setError('Bitte ein Produkt wählen.')
      return
    }
    const priceCents = euroStringToCents(unitPrice)
    if (priceCents == null) {
      setError('Verkaufspreis ungültig.')
      return
    }
    add.mutate(
      { componentProductId: productId, unitPriceCents: priceCents },
      {
        onSuccess: () => {
          toast({ title: 'Komponente ergänzt', tone: 'success' })
          onOpenChange(false)
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.'),
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Komponente hinzufügen"
      description="Welcher Vertragsbaustein wird in diesem Tarif zu welchem Preis angeboten?"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={add.isPending}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={add.isPending}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
            {error}
          </div>
        ) : null}
        <Select
          label="Komponentenprodukt"
          value={productId}
          onValueChange={setProductId}
          options={options}
        />
        <Input
          label="Verkaufspreis je Einheit"
          hint="Mengenstaffeln und Einkaufspreis kommen nach dem Anlegen."
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.currentTarget.value)}
        />
      </div>
    </Modal>
  )
}
